import type { Plugin } from 'vite';

/**
 * THIS FILE MUST STAY SELF-CONTAINED — no runtime `import`, only `import type`.
 *
 * `vite.config.ts` imports it as `@pypestream/utils/build-env`, and Vite leaves
 * a bare import EXTERNAL when it bundles a config file: Node then loads this
 * `.ts` directly, stripping the types (Node >= 22.6, and 24 is pinned by
 * `.nvmrc`, every workflow's `node-version`, every `docker/Dockerfile.*`
 * `ARG NODE_VERSION`, and the root `package.json` `engines.node` — which is
 * what the Vercel path reads, since it runs no Dockerfile and no workflow
 * `node-version`). Node's ESM resolver does not fill in extensions, so a
 * relative `import './x'` here fails the whole build with
 * `Cannot find module …/x`. Measured: the first version of this module was two
 * files and did exactly that. Type-stripping also refuses to run inside
 * `node_modules`, and the reason it applies here is that the workspace symlink
 * resolves to a realpath OUTSIDE it — measured with `require.resolve` from all
 * three consuming packages. `guards the load path vite.config depends on` in
 * the test file pins the no-import half.
 */

/**
 * Why this guard exists (PSTUDIO-4260).
 *
 * Three apps declare `FE` as an `envPrefix`, so every `FE_*` variable present
 * at build time lands in Vite's env object and any one of them that the source
 * reads through `import.meta.env` is INLINED into the JavaScript the browser
 * downloads. `uat-deploy-apps.yml` passed `secrets.FE_SENTRY_PRO_STUDIO_AUTH_TOKEN`
 * as the value of `FE_PRO_STUDIO_SENTRY_DSN`, and `main.tsx` reads that variable
 * — so the Sentry auth token was served to every UAT visitor from 2026-04-23
 * until it was found. Measured on the deployed bundle 2026-09-21: one Sentry
 * token-prefix occurrence in `prostudio.uat.pypestream.dev`, zero in candidate,
 * qa1, main and production, each of which carried a real DSN host instead.
 *
 * Nothing in the pipeline could have caught it: a DSN is public by design, so
 * the variable is not secret-scanned, and the value is only wrong RELATIVE to
 * the name it was given. The guard closes exactly that gap, at the one moment
 * where both halves are known — after Vite resolved its env, before it
 * transformed a single module.
 *
 * WHAT THIS GUARD DOES NOT DO, stated rather than implied:
 *
 * - It judges the env Vite RESOLVED, so it never runs when a build does not:
 *   `build` is an nx `cacheableOperations` target and no `namedInputs` entry
 *   names an env variable, so a cache hit skips Vite entirely. The three apps
 *   now list their Sentry variables as `{ "env": … }` inputs, which makes a
 *   changed value miss the cache and therefore reach this check.
 * - It says nothing about LOGS. The value is already in the build environment
 *   by the time this runs, and `DEBUG=vite:config` prints the whole resolved
 *   config. What throwing buys is that the value never reaches a build
 *   ARTIFACT — and the `generateBundle` pass is what makes that a checked fact
 *   rather than an inference from the candidate rules.
 * - An EMPTY value is not judged at all (see the loop below), so a secret that
 *   was renamed out of existence still produces a silent deploy — the quieter
 *   half of PSTUDIO-4260. Refusing it here would break every local build
 *   without credentials; catching it belongs in the workflow that resolves the
 *   value; `preview-deploy` and `env-deploy` refuse a missing or empty key.
 */

export type PublicEnvProblemReason =
  | 'secret-in-public-env'
  | 'secret-shaped-name'
  | 'malformed-dsn';

export interface PublicEnvProblem {
  name: string;
  reason: PublicEnvProblemReason;
  /** Safe to print: it names the variable and never quotes its value. */
  message: string;
}

/** Whether a problem must fail a command that produces no client artifact. */
export const isArtifactOnlyProblem = (problem: PublicEnvProblem): boolean =>
  problem.reason === 'malformed-dsn';

/**
 * Variables that legitimately hold a secret AND carry a public prefix.
 *
 * Two families, both verified against the workflows that set them:
 *
 * - `FE_SENTRY_*_AUTH_TOKEN` is read by `vite.config.ts` through `process.env`
 *   for `sentryVitePlugin` (source-map upload), and by nothing through
 *   `import.meta.env` — so Vite lists it but never inlines it. Measured: the
 *   local pro-studio `dist` holds the DSN host and no token prefix at all.
 * - `FE_GQL_ADMIN_SECRET*` is the Hasura admin secret, set for every
 *   environment by `new-ci.yml` and exported to the whole build matrix by
 *   `build-maker/action.yml`. Its only readers are `api-services/codegen.ts`
 *   and `codegen.limited-access-universal.ts`, which graphql-codegen runs as
 *   configs — no application module imports either, so nothing inlines them.
 *
 * This is an allowance about the NAME, and it is the weak point of judging
 * CANDIDATES: rename the workflow line and the allowance follows the name, not
 * the intent. It is still the right trade for that pass — without it the guard
 * would fail every build that has source-map upload or codegen configured, and
 * a guard that fails every build is a guard somebody deletes.
 *
 * What closes the hole is that being on this list does NOT exempt a value from
 * the `generateBundle` pass: an allowed name may hold a secret, and that is
 * precisely the reason to check the emitted chunks do not contain it.
 */
export const BUILD_TIME_ONLY_SECRETS = [
  'FE_SENTRY_MANAGER_AUTH_TOKEN',
  'FE_SENTRY_PRO_STUDIO_AUTH_TOKEN',
  'FE_GQL_ADMIN_SECRET',
  'FE_GQL_ADMIN_SECRET_MAIN',
  'FE_GQL_ADMIN_SECRET_CANDIDATE',
  'FE_GQL_ADMIN_SECRET_QA1',
  'FE_GQL_ADMIN_SECRET_UAT',
  'FE_GQL_ADMIN_SECRET_PROD',
  'FE_GQL_ADMIN_SECRET_PROD_EU',
] as const;

/**
 * Shapes that are a credential whatever variable they sit in.
 *
 * Sentry prefixes its tokens by type — see `src/sentry/types/token.py`, which
 * defines four, one letter each for user, organization, user-app and
 * integration. The token this ticket found leaking is an ORGANIZATION token;
 * the pattern covers all four, because the rotation that follows this incident
 * can legitimately issue any of them.
 *
 * NOTE: the prefixes are deliberately not spelled out literally anywhere in
 * this file. `sentry-init` imports the DSN predicate below, so this module
 * reaches the client bundle's SOURCE MAP — and a literal example there is
 * indistinguishable, to `grep`, from a real leaked token. That grep is how
 * PSTUDIO-4260 was found in the first place, and the diagnostic is worth more
 * than the example. Measured: with the examples written out, a fresh
 * pro-studio `dist` matched the token pattern in `index-*.js.map`.
 *
 * Deliberately NOT anchored at `^`: a token reaches a variable inside a
 * composed value just as easily as alone (a `--auth-token=<token>` CLI
 * argument, and composing workflow strings by hand is how PSTUDIO-4260
 * happened). LIMITATION, stated rather than implied: Sentry's older 64-hex
 * tokens have no prefix to match on, and a pattern loose enough to catch them
 * would also catch hashes and ids, so this guard does not see them.
 */
const SECRET_VALUE_PATTERNS: readonly RegExp[] = [/sntry[suai]_/];

/**
 * A NAME that promises a credential, whatever its value looks like.
 *
 * The value rule above only recognises one vendor's prefix, so it cannot see a
 * Hasura admin secret, a database password or a private key — all of which are
 * random strings. Deliberately narrow: `_API_KEY` and `_CLIENT_TOKEN` are
 * PUBLIC by design here (`FE_LAUNCH_DARKLY_API_KEY`, `FE_POSTHOG_API_KEY`,
 * `FE_PRODUKTLY_CLIENT_TOKEN`), so matching a bare `_KEY` or `_TOKEN` would
 * fail every real build — verified against every public-prefixed variable the
 * workflows assign.
 */
const SECRET_NAME = /_(SECRET|PASSWORD|PRIVATE_KEY|AUTH_TOKEN)(_[A-Z0-9]+)*$/;

/** A variable whose NAME promises a DSN, and whose value can therefore be judged. */
const DSN_VARIABLE = /_SENTRY_DSN$/;

/**
 * Why a value cannot be the DSN `Sentry.init` accepts. One string per cause,
 * printed verbatim, because the four causes have four different fixes — an
 * unexpanded `${{ secrets.… }}` means the secret is not defined in the
 * repository, which no amount of reformatting the value will solve.
 */
export type DsnDefect =
  | 'it is wrapped in whitespace'
  | 'it is not a URL'
  | 'its protocol is not http(s)'
  | 'it has no public key before the @'
  | 'it has no project id in the path'
  | 'its project id is not a number';

/**
 * `{PROTOCOL}://{PUBLIC_KEY}@{HOST}/{PROJECT_ID}` — the form `Sentry.init`
 * accepts, kept deliberately as tight as Sentry's own `DSN_REGEX`
 * (`@sentry/core/.../dsn.js`), which is `\w+` for the key and `^\d+$` for the
 * project id. Measured against that regex: `new URL` alone accepts
 * `"  https://k@h/1"` and `"https://key-with-dash@h/1"`, both of which Sentry
 * REFUSES — i.e. exactly the silent-deploy case this guard exists to stop.
 *
 * A legacy `public:secret` pair and a self-hosted `http` host stay valid, and
 * a self-hosted path prefix is allowed (the project id is the LAST segment),
 * because Sentry accepts all three. LIMITATION: `http` is accepted although no
 * environment here is self-hosted, so a plaintext DSN would not be refused;
 * narrowing it would break the support this repository claims rather than a
 * case it actually has.
 */
export const findDsnDefect = (value: string): DsnDefect | null => {
  // Before `new URL`, which trims surrounding whitespace and would hide it.
  if (value !== value.trim()) return 'it is wrapped in whitespace';

  let url: URL;

  try {
    url = new URL(value);
  } catch {
    return 'it is not a URL';
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return 'its protocol is not http(s)';
  }

  if (!/^\w+$/.test(url.username)) return 'it has no public key before the @';

  const segments = url.pathname.split('/').filter(Boolean);
  const projectId = segments[segments.length - 1];

  if (projectId === undefined) return 'it has no project id in the path';
  if (!/^\d+$/.test(projectId)) return 'its project id is not a number';

  return null;
};

const looksLikeSecret = (value: string): boolean =>
  SECRET_VALUE_PATTERNS.some((pattern) => pattern.test(value));

const WHERE_IT_IS_SET =
  'Check where this variable is set — the workflow line under ' +
  '.github/workflows/, or your local .env.local';

/**
 * Judges the env Vite is about to hand to the client.
 *
 * One problem per variable at most, in the order secret-value > secret-name >
 * DSN shape: a token is not a URL either, and reporting it as a malformed DSN
 * would send the reader to the wrong fix.
 *
 * The allowance is checked FIRST and exempts a variable from every rule, not
 * only from the secret ones. That is the whole meaning of "this name is allowed
 * to hold a secret"; leaving it inside one branch made the exemption depend on
 * which rule happened to match first.
 */
export const findPublicEnvProblems = (
  env: Record<string, unknown>,
  buildTimeOnly: readonly string[] = BUILD_TIME_ONLY_SECRETS
): PublicEnvProblem[] => {
  const problems: PublicEnvProblem[] = [];

  for (const [name, rawValue] of Object.entries(env)) {
    // Vite's own entries (`DEV`, `PROD`, `SSR`) are booleans.
    if (typeof rawValue !== 'string' || rawValue === '') continue;

    if (buildTimeOnly.includes(name)) continue;

    if (looksLikeSecret(rawValue)) {
      problems.push({
        name,
        reason: 'secret-in-public-env',
        message:
          `${name} holds a value shaped like a Sentry auth token. Every ` +
          `variable with a public prefix that the source reads through ` +
          `import.meta.env is inlined into the client bundle, so this would ` +
          `be served to every visitor. ${WHERE_IT_IS_SET}, and rotate the ` +
          `token if a build already shipped.`,
      });
      continue;
    }

    if (SECRET_NAME.test(name)) {
      problems.push({
        name,
        reason: 'secret-shaped-name',
        message:
          `${name} is named like a credential but carries a public prefix, ` +
          `so it is a candidate for inlining into the client bundle. Either ` +
          `add it to BUILD_TIME_ONLY_SECRETS with the reason nothing reads ` +
          `it through import.meta.env, or stop putting it in the build ` +
          `environment. ${WHERE_IT_IS_SET}.`,
      });
      continue;
    }

    if (DSN_VARIABLE.test(name)) {
      const defect = findDsnDefect(rawValue);

      if (defect !== null) {
        problems.push({
          name,
          reason: 'malformed-dsn',
          message:
            `${name} is not a Sentry DSN — ${defect}. It must read ` +
            `https://<publicKey>@<host>/<projectId>. Sentry.init refuses a ` +
            `value it cannot parse, so the deploy would report nothing at ` +
            `all. ${WHERE_IT_IS_SET}.`,
        });
      }
    }
  }

  return problems;
};

export interface PublicEnvReportContext {
  /** Vite's `config.command`, so the first line cannot claim a build that is not running. */
  command?: string;
  /** Vite's `config.root`, so one log covering three apps says which one refused. */
  root?: string;
}

export const formatPublicEnvProblems = (
  problems: readonly PublicEnvProblem[],
  context: PublicEnvReportContext = {}
): string => {
  const action = context.command === 'build' ? 'build' : 'start';
  const where = context.root === undefined ? '' : ` ${context.root}`;

  return [
    `pypestream:public-env-guard — refusing to ${action}${where}: the ` +
      `environment would put a secret, or an unusable value, into the client ` +
      `bundle.`,
    ...problems.map((problem) => `  - ${problem.message}`),
  ].join('\n');
};

export interface PublicEnvGuardOptions {
  /** REPLACES the default allowance list; it does not extend it. */
  buildTimeOnly?: readonly string[];
}

/**
 * Below this length a value is not distinctive enough to search a bundle for:
 * a short string matches by coincidence, and a false refusal on every build is
 * how a guard gets deleted. Every credential this repository sets is far
 * longer — the shortest, a Hasura admin secret, is 32 characters.
 */
const SEARCHABLE_SECRET_LENGTH = 12;

/**
 * The values an emitted chunk must not contain, keyed by the variable they came
 * from so a report can name it without quoting it.
 *
 * Deliberately built from the ALLOWED names as well as the refused ones — that
 * is the whole point of this pass. `findPublicEnvProblems` judges CANDIDATES
 * and therefore needs an allowance list to stay workable; this judges the
 * OUTPUT, where a value is either in the artifact or it is not. A name being
 * allowed to hold a secret is exactly the reason to check that its secret did
 * not ship.
 */
export const collectBundleForbiddenValues = (
  env: Record<string, unknown>,
  buildTimeOnly: readonly string[] = BUILD_TIME_ONLY_SECRETS
): Map<string, string> => {
  const forbidden = new Map<string, string>();

  for (const [name, rawValue] of Object.entries(env)) {
    if (typeof rawValue !== 'string') continue;
    if (rawValue.length < SEARCHABLE_SECRET_LENGTH) continue;

    if (
      looksLikeSecret(rawValue) ||
      SECRET_NAME.test(name) ||
      buildTimeOnly.includes(name)
    ) {
      forbidden.set(rawValue, name);
    }
  }

  return forbidden;
};

export interface BundleLeak {
  /** The variable the value came from. The value itself is never carried. */
  name: string;
  fileName: string;
}

export const findBundleLeaks = (
  forbidden: ReadonlyMap<string, string>,
  files: ReadonlyMap<string, string>
): BundleLeak[] => {
  const leaks: BundleLeak[] = [];

  for (const [fileName, text] of files) {
    for (const [value, name] of forbidden) {
      if (text.includes(value)) leaks.push({ name, fileName });
    }
  }

  return leaks;
};

export const formatBundleLeaks = (leaks: readonly BundleLeak[]): string =>
  [
    'pypestream:public-env-guard — refusing to emit: a credential from the ' +
      'build environment is present VERBATIM in the output. This is the leak ' +
      'itself, not a heuristic about it — rotate the credential and remove ' +
      'whatever reads it into client code.',
    ...leaks.map((leak) => `  - ${leak.name} appears in ${leak.fileName}`),
  ].join('\n');

/**
 * Runs in `configResolved` — after Vite merged `.env` files with `process.env`
 * and applied `envPrefix`, and before the first module is transformed. Throwing
 * here means the offending value never reaches an artifact or a cache (for what
 * it does NOT promise, see the module docblock).
 *
 * `enforce: 'pre'` is load-bearing rather than cosmetic: Vite calls
 * `getSortedPluginHooks('configResolved').map((hook) => hook(resolved))`
 * synchronously inside `Promise.all`, so a synchronous throw ends the walk and
 * no later plugin's `configResolved` — `sentryVitePlugin`'s included — runs.
 *
 * `config.env` is the set of CANDIDATES rather than proof of an inline: Vite
 * replaces the `import.meta.env.X` expressions the source actually contains.
 * Judging the candidates is the stricter reading, and the allowance list above
 * is what keeps it workable.
 *
 * SEVERITY DEPENDS ON THE COMMAND, and the split is the point. A credential is
 * refused under every command, because `vite dev` serves `import.meta.env` to
 * the browser too. A malformed DSN is refused only by `build`, because that is
 * the only command that produces the artifact the shape protects: `main.tsx`
 * gates `Sentry.init` on `!isDevelopment`, so under `serve` the value is inert,
 * and failing there would stop `yarn start:*` — and, where a package keeps its
 * vitest config inside `vite.config.ts`, `yarn test` — over a value nothing
 * uses. A guard that fails a command it has no stake in is a guard somebody
 * deletes.
 *
 * TWO PASSES, and the second is the exact one. `configResolved` judges the
 * candidates — cheap, early, and the only pass that can stop a value before it
 * is transformed, but it reasons about names and shapes, which is why it needs
 * an allowance list at all. `generateBundle` then searches the emitted chunks
 * for those values VERBATIM: no heuristic, no allowance, and it covers every
 * inlining channel at once — `import.meta.env`, `define`, a `?raw` import, the
 * HTML transform. It is a rollup hook, so it runs on `build` only, which is
 * the only command that emits anything.
 */
export const publicEnvGuard = (options: PublicEnvGuardOptions = {}): Plugin => {
  let forbidden: Map<string, string> = new Map();

  return {
    name: 'pypestream:public-env-guard',
    enforce: 'pre',
    configResolved(config) {
      forbidden =
        config.command === 'build'
          ? collectBundleForbiddenValues(config.env, options.buildTimeOnly)
          : new Map();

      const problems = findPublicEnvProblems(config.env, options.buildTimeOnly);

      if (problems.length === 0) return;

      const fatal =
        config.command === 'build'
          ? problems
          : problems.filter((problem) => !isArtifactOnlyProblem(problem));

      if (fatal.length > 0) {
        throw new Error(
          formatPublicEnvProblems(fatal, {
            command: config.command,
            root: config.root,
          })
        );
      }

      config.logger.warn(
        formatPublicEnvProblems(problems, {
          command: config.command,
          root: config.root,
        })
      );
    },
    generateBundle(_options, bundle) {
      if (forbidden.size === 0) return;

      const files = new Map<string, string>();

      for (const [fileName, output] of Object.entries(bundle)) {
        if (output.type === 'chunk') {
          files.set(fileName, output.code);
        } else if (typeof output.source === 'string') {
          files.set(fileName, output.source);
        }
      }

      const leaks = findBundleLeaks(forbidden, files);

      if (leaks.length > 0) throw new Error(formatBundleLeaks(leaks));
    },
  };
};
